import moment from 'moment';
import { list } from '@fitogest/reactive-form-builder/utils';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    ReactiveFieldLogicContext,
    ReactiveDropdownCatalogs,
} from '@fitogest/reactive-form-builder/interfaces';
import {
    createFincaScopedLegacyFilter,
    createParcelasScopedLegacyFilter,
} from '../../../shared/tareas-location-dropdown.datasource';
import { EcorregimenModel } from '../../../../../../models/tareas/ecorregimen.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';

export class EcorregimenesFormDefinition {
    public readonly appName = environment.appName;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelaName = environment.features.parcelaName;
    public readonly hasFincas = environment.features.hasFincas;
    public readonly hasClients = environment.features.hasClients;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';
    public readonly showTareasFitoHoras = environment.features.showTareasFitoHoras;
    public readonly tareasHorasTotales = environment.features.tareasHorasTotales;

    public formType!: FormType;
    public model: Partial<EcorregimenModel> = {};
    public formFields!: Form<EcorregimenModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public tareasMaquinariaMalasHierbas: DropdownData<string> = list<string>(
        ['...', null],
        'Fangueo',
        'Pase de Ganchos 1',
        'Fresadora',
        'Nivelación',
        'Pase de Ganchos 2',
    );

    public tareasMaquinariaPalustre: DropdownData<string> = list<string>(
        ['...', null],
        'Abonar (Mecánica)',
        'Fangueo / Fresadora (Mecánica)',
        'Hacer Márgenes (Mecánica)',
        'Trailla (Mecánica)',
        'Láser (Mecánica)',
        'Cultivador (Mecánica)',
        'Rotovator (Mecánica)',
        'Multiapero (Mecánica)',
        'Abrir Boqueras (Escardaquímica)',
        'Bordoma (Escardaquímica)',
        'Transplantar (Escardaquímica)',
        'Escarda (Escardaquímica)',
        'Desbroce Cursos de Agua (Escardaquímica)',
        'Siega Orillas (Escardaquímica)',
        'Quemar Paja (Escardaquímica)',
    );

    public maquinariasMalasHierbas: DropdownData<string> = list<string>(['...', null], 'Fresadora', 'Gábia', 'Ganchos', 'Láser');
    public operacionesPaja: DropdownData<string> = list<string>(
        ['...', null],
        'Secado',
        'Quema',
        'Empaca',
        'Enterrado',
        'Retirada',
        'Otros',
    );

    public fieldNames: FormField<EcorregimenModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<EcorregimenModel>) {
        this.formType = +type;
        this.model = model;
        this.ensureDefaultDropdownValues();

        this.formFields = {
            name: 'tareas-ecorregimenes',
            header: `Datos de la tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
        };
    }

    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: ['id_finca', 'id_sector', 'id_parcelas'],
            deferred: [],
            lazy: [],
        };
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    private getFormSections(): FormSection<EcorregimenModel>[] {
        const sections: FormSection<EcorregimenModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: 'Cultivo Arroz',
                width: 12,
                isVisible: true,
                fields: this.getArrozFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getPalustreFields(),
                order: 3,
            },
            {
                name: 'Control de Malas Hierbas en época de no cultivo',
                width: 12,
                isVisible: true,
                fields: this.getMalasHierbasFields(),
                order: 4,
            },
            {
                name: 'Actuación Rastrojo',
                width: 12,
                isVisible: this.appName !== 'avafitogest',
                fields: this.getRastrojoFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => {
                            const fincaValue = it.value as unknown as FincasModel | null;

                            if (!this.hasClients || this.formType === FormType.EDITION || !this.userCanSeeClientes) {
                                return true;
                            }

                            if (this.client?.id) {
                                return String(fincaValue?.id_cliente ?? '') === String(this.client.id);
                            }

                            return true;
                        },
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true, isVisible: this.hasFincas }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : this.getDisplaySectorFieldName(),
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_sector' : this.getDisplaySectorFieldName(),
                        kind: 'filter'
                    }
                ],
                label: this.sectorName,
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: createFincaScopedLegacyFilter<EcorregimenModel>(),
                    },
                },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    }
                ],
                label: this.parcelaName,
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: createParcelasScopedLegacyFilter<EcorregimenModel>({
                            sectorFieldName: 'id_sector',
                        }),
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'fecha_entrada',
                label: 'Fecha Entrada Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 4,
            },
            {
                name: 'fecha_salida',
                label: 'Fecha Salida Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 5,
            },
            {
                name: 'fecha_seca_cultivo_agua',
                label: 'Fecha Seca Cultivo de Agua',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 6,
            },
            {
                name: 'fecha_seca_arroz',
                label: 'Fecha Seca Arroz',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 7,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getArrozFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'fecha_siembra',
                label: 'Fecha Siembra',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'dosis_siembra',
                label: 'Dosis Siembra (kg/ha)',
                input: { type: InputType.NUMERIC, isEditable: true },
                helpText: 'Mínimo 115 kg/Ha',
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'avafitogest' }),
                order: 2,
            },
            {
                name: 'fecha_recoleccion_arroz',
                label: 'Fecha Recolección',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'control_mecanico_vn',
                label: 'Control mecánico de la Vegetación Natural Palustre próxima a cursos de agua',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getPalustreFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'tareas_palustre',
                label: 'Tareas Palustre',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tareasMaquinariaPalustre,
                    defaultValue: 'Hacer Márgenes (Mecánica)',
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createPalustreVisibilityDependencies('tareas_palustre'),
                visibleWhen: {
                    dependsOn: ['control_mecanico_vn'],
                    resolve: (context): boolean => this.isControlMecanicoVisibleFromContext(context),
                },
                order: 1,
            },
            {
                name: 'fecha_paluestre',
                label: 'Fecha Palustre',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createPalustreVisibilityDependencies('fecha_paluestre'),
                visibleWhen: {
                    dependsOn: ['control_mecanico_vn'],
                    resolve: (context): boolean => this.isControlMecanicoVisibleFromContext(context),
                },
                order: 2,
            },
            {
                name: 'maquinaria_palustre',
                label: 'Maquinaria',
                input: {
                    type: InputType.TEXT,
                    defaultValue: this.getInitialPalustreMaquinariaValue(),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createPalustreVisibilityDependencies('maquinaria_palustre'),
                visibleWhen: {
                    dependsOn: ['control_mecanico_vn'],
                    resolve: (context): boolean => this.isControlMecanicoVisibleFromContext(context),
                },
                order: 3,
            },
            {
                name: 'observaciones_palustre',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createPalustreVisibilityDependencies('observaciones_palustre'),
                visibleWhen: {
                    dependsOn: ['control_mecanico_vn'],
                    resolve: (context): boolean => this.isControlMecanicoVisibleFromContext(context),
                },
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getMalasHierbasFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'tareas_malashierbas',
                label: 'Tareas',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tareasMaquinariaMalasHierbas,
                    defaultValue: 'Fangueo',
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'fecha_malashierbas',
                label: 'Fecha',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'id_maquinaria_malashierbas',
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.maquinariasMalasHierbas,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'observaciones_malashierbas',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getRastrojoFields(): FormField<EcorregimenModel>[] {
        const fields: FormField<EcorregimenModel>[] = [
            {
                name: 'fecha_recoleccion_rastrojo',
                label: 'Fecha Recolección',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 1,
            },
            {
                name: 'operacion_paja',
                label: 'Operación',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.operacionesPaja,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 2,
            },
            {
                name: 'fecha_operacion',
                label: 'Fecha Operación',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' }),
                order: 3,
            },
            {
                name: 'hora_inicio',
                label: 'Hora Inicio Quema',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' && this.showTareasFitoHoras }),
                order: 4,
            },
            {
                name: 'hora_fin',
                label: 'Hora Fin Quema',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'avafitogest' && this.showTareasFitoHoras }),
                order: 5,
            },
            {
                name: 'horas_totales',
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({ isVisible: this.appName === 'agroleza' }),
                order: 6,
            },
        ];

        return this.registerVisibleFields(fields);
    }


    private getDisplaySectorFieldName(): string {
        return this.appName === 'avafitogest' ? 'sectores' : 'sector';
    }

    private getHiddenFields(): FormField<EcorregimenModel>[] {
        return [
            this.hiddenField('id', this.model.id),
            this.hiddenField('id_cliente', this.getInitialClientId()),
            this.hiddenField('id_usuario', this.getInitialUserId()),
            this.hiddenField('adjunto_guia', this.model.adjunto_guia),
            this.hiddenField('fecha_inicio', this.model.fecha_inicio),
            this.hiddenField('fecha_fin', this.model.fecha_fin),
            this.hiddenField('fecha_fin_inundacion_invernal', this.model.fecha_fin_inundacion_invernal),
            this.hiddenField('fecha_seca_inicio_intermitente', this.model.fecha_seca_inicio_intermitente),
            this.hiddenField('fecha_seca_fin_intermitente', this.model.fecha_seca_fin_intermitente),
            this.hiddenField('hora_inicio_intermitente', this.model.hora_inicio_intermitente),
            this.hiddenField('hora_fin_intermitente', this.model.hora_fin_intermitente),
        ];
    }

    private hiddenField(name: keyof EcorregimenModel & string, defaultValue: unknown): FormField<EcorregimenModel> {
        const input: FormField<EcorregimenModel>['input'] = {
            type: InputType.HIDDEN,
            isEditable: false,
        };

        if (defaultValue !== undefined) {
            input.defaultValue = defaultValue as string | number | boolean | Date | EcorregimenModel | null;
        }

        return {
            name,
            label: '',
            input,
            width: 1,
            conditions: this.conditions({ isVisible: true }),
            order: 1,
        };
    }

    private registerVisibleFields(fields: FormField<EcorregimenModel>[]): FormField<EcorregimenModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private conditions(options: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: options.isRememberedOnDuplicate ?? true,
            isRequired: options.isRequired ?? false,
            isRetained: options.isRetained ?? false,
            hasUndoEnabled: options.hasUndoEnabled ?? false,
            isVisible: options.isVisible ?? true,
        };
    }

    private ensureDefaultDropdownValues(): void {
        if (!this.model.tareas_palustre) {
            this.model.tareas_palustre = 'Hacer Márgenes (Mecánica)';
        }

        if (!this.model.tareas_malashierbas) {
            this.model.tareas_malashierbas = 'Fangueo';
        }

        this.ensureStaticDropdownContainsValue(
            this.maquinariasMalasHierbas,
            this.model.id_maquinaria_malashierbas ?? this.model.maquinaria_malashierbas ?? this.model.nombre_maquinaria_malashierbas,
        );
    }

    private getInitialPalustreMaquinariaValue(): string {
        return this.normalizeOptionalText(
            this.model.maquinaria_palustre ??
                this.model.tareas_maquinaria_palustre ??
                this.model.id_maquinaria_palustre,
        );
    }

    private ensureStaticDropdownContainsValue(dropdownData: DropdownData<string>, value: unknown): void {
        const normalizedValue = this.normalizeOptionalText(value);

        if (normalizedValue === '') {
            return;
        }

        const exists = [...(dropdownData.filtered ?? []), ...(dropdownData.values ?? [])].some((option) =>
            this.normalizeOptionalText(option.value ?? option.label) === normalizedValue,
        );

        if (exists) {
            return;
        }

        const option = {
            label: normalizedValue,
            value: normalizedValue,
            raw: normalizedValue,
            valueAliases: [normalizedValue],
        };

        dropdownData.filtered = [...(dropdownData.filtered ?? []), option];
        dropdownData.values = [...(dropdownData.values ?? []), option];
    }

    private normalizeOptionalText(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        const normalizedValue = String(value).trim();

        if (normalizedValue === '' || normalizedValue === '0' || normalizedValue.toLowerCase() === 'null') {
            return '';
        }

        return normalizedValue;
    }

    private createPalustreVisibilityDependencies(target: string): NonNullable<FormField<EcorregimenModel>['dependencies']> {
        return [
            {
                source: 'control_mecanico_vn',
                target,
                kind: 'visibility',
            },
        ];
    }

    private isControlMecanicoVisibleFromContext(context?: ReactiveFieldLogicContext<EcorregimenModel>): boolean {
        return this.isTruthy(context?.getValue('control_mecanico_vn'));
    }

    private isTruthy(value: unknown): boolean {
        return value === true || value === 1 || value === '1' || value === 'true';
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid()) {
            console.error('Invalid time format. Expected HH:mm.', { startTime, endTime });
            return '';
        }

        if (end.isBefore(start)) {
            console.warn('End time is before start time.', { startTime, endTime });
            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private getInitialClientId(): string {
        if (this.user.tipo === 'comunero') {
            return this.user.id_cliente ?? '';
        }

        return String(this.model.id_cliente ?? this.client?.id ?? '');
    }

    private getInitialUserId(): string {
        return String(this.model.id_usuario ?? this.user.id ?? '');
    }
}
